import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { EntityWithLabel } from '../../api/ApiTypes';
import { Button, usePrompt } from '../../ui';
import { ResultInfoForMutation } from '../../ui/ResultInfo';
import { AppEvents } from '../AppEvents';
import { useResolve } from '../Container';
import { Copy, Flexer, Spacer } from '../dashboard/elements/LayoutElements';
import { Popup, PopupProps } from '../popups/Popup';
import { TeamLocationSelectView } from './TeamLocationSelectView';

const useBoardMoveMutation = () => {
    const api = useResolve(ApiClient);
    const evts = useResolve(AppEvents);

    return useMutation({
        mutationFn: async (params: { boardId: string, toId: string }) => {
            return await api.exec(v2.boards.move({
                boardId: params.boardId,
                toId: params.toId
            }));
        },
        onSuccess(data) {
            evts.push('board:move', data);
        }
    });
};

export const newBoardMovePopup = (board: EntityWithLabel) => ({ ui: NewMoveBoardPopup, args: { board } });

export const NewMoveBoardPopup = React.memo(({ action, dismiss, args }: PopupProps) => {
    const { board } = args;

    const mutation = useBoardMoveMutation();

    const [location, setLocation] = useState(null as { workspaceId: string, folderId?: string } | null);
    const invalid = !location;

    const onAction = (confirm: boolean) => {
        if (confirm) {
            if (!mutation.isLoading) {
                mutation
                    .mutateAsync({
                        boardId: board.id,
                        toId: location.folderId || location.workspaceId
                    })
                    .then(() => action(true));
            }
        }
        else {
            dismiss();
        }
    };

    const promptRef = usePrompt({
        onAction: mutation.isLoading ? null : onAction
    });

    return <Flexer ref={promptRef}>
        <Popup w={32}>
            <Popup.Close onAction={() => onAction(false)} />
            <Popup.Title>Move Board</Popup.Title>
            <Popup.Content>
                <Copy>
                    Where do you want to move this board?
                </Copy>
                <TeamLocationSelectView
                    mode="user"
                    onChange={setLocation}
                />
                <Spacer />
                <ResultInfoForMutation mutation={mutation} />
            </Popup.Content>
            <Popup.Actions>
                <Button disabled={invalid || mutation.isLoading} variant="primary" onAction={() => onAction(true)}>
                    {mutation.isLoading ? 'Moving...' : 'Move'}
                </Button>
                <Button disabled={mutation.isLoading} onAction={() => onAction(false)}>
                    Cancel
                </Button>
            </Popup.Actions>
        </Popup>
    </Flexer>;
});
